
import { cn } from "@/lib/utils"
import { isDebugEnabled } from "@/utils/debug"
import React, { useState, useEffect, useRef, useMemo } from "react";
import { ImageOff } from "lucide-react";
import { Skeleton } from "./skeleton";
import { Spinner } from "./Spinner";
import { isSupabasePublicStorageUrl, getRenderUrlFromPublicUrl, buildSrcSetFromPublicUrl } from "@/utils/image-storage/remote";

// URL cache to prevent regeneration across component instances
const urlCache = new Map<string, { src: string; srcSet?: string; timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Helper to generate cache key
const getCacheKey = (url: string, params: any) => {
  return `${url}_${JSON.stringify(params)}`;
};

// Cache cleanup to prevent memory leaks
const cleanupCache = () => {
  const now = Date.now();
  const expiredKeys: string[] = [];
  
  for (const [key, value] of urlCache.entries()) {
    if (now - value.timestamp > CACHE_DURATION) {
      expiredKeys.push(key);
    }
  }
  
  expiredKeys.forEach(key => urlCache.delete(key));
  
  if (expiredKeys.length > 0 && isDebugEnabled()) {
    console.log(`[Image] Cleaned up ${expiredKeys.length} expired cache entries`);
  }
};

// Run cleanup every 5 minutes
setInterval(cleanupCache, CACHE_DURATION);

export interface ImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  width?: number;
  height?: number;
  onError?: React.ReactEventHandler<HTMLImageElement>;
  objectFit?: "cover" | "contain";
  objectPosition?: string; // For fine-grained cropping control (e.g. "center top", "left center")
  loading?: "lazy" | "eager";
  priority?: boolean; // For above-the-fold images
  showSkeleton?: boolean; // Whether to show skeleton instead of spinner
  fallbackSrc?: string; // Optional fallback image source
  instant?: boolean; // Instantly render without spinner/skeleton and no fade-in
  enableOptimization?: boolean; // Opt-in for Supabase render transformations
  resize?: "contain" | "cover" | "fill"; // Supabase resize mode
  optimizationMode?: "resize" | "format" | "none"; // Control optimization behavior
}

const Image = ({ 
  className, 
  alt, 
  onError, 
  src, 
  objectFit = "cover",
  objectPosition = "center", 
  loading = "lazy", 
  priority = false,
  showSkeleton = false,
  fallbackSrc,
  instant = false,
  enableOptimization = true, // Enable by default since we're using the SDK now
  resize,
  optimizationMode = "resize", // Default to current behavior
  width: explicitWidth,
  height: explicitHeight,
  sizes: sizesProp,
  srcSet: srcSetProp,
  ...props 
}: ImageProps) => {
  const [isLoading, setIsLoading] = useState(!instant);
  const [showSpinner, setShowSpinner] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [currentSrc, setCurrentSrc] = useState<string | undefined>(src);
  const [optEnabled, setOptEnabled] = useState(enableOptimization); // Internal opt state
  const [disableTransforms, setDisableTransforms] = useState(false); // Disable transforms after 400/5xx errors
  const loadStartTime = useRef<number>(Date.now());
  const spinnerTimeout = useRef<NodeJS.Timeout>();
  const attemptedFixRef = useRef({ triedRaw: false, triedRender: false, loggedFetch: false });
  const actualAttemptedUrl = useRef<string>(); // Track what URL was actually tried

  const debugFetch = async (url?: string) => {
    if (!url || attemptedFixRef.current.loggedFetch) return;
    attemptedFixRef.current.loggedFetch = true;
    try {
      const res = await fetch(url, { cache: 'reload', mode: 'cors' });
      if (isDebugEnabled()) {
        console.warn('[Image] fetch status', {
          url,
          status: res.status,
          ok: res.ok,
          contentType: res.headers.get('content-type'),
        });
      }

      // Log the JSON error response for better diagnostics on 400/5xx errors
      if (!res.ok) {
        try {
          const errorBody = await res.json();
          if (isDebugEnabled()) console.warn('[Image] transform service error response:', errorBody);
        } catch {
          const textBody = await res.text();
          if (isDebugEnabled()) console.warn('[Image] transform service error (non-JSON):', textBody);
        }
      }
    } catch (err) {
      if (isDebugEnabled()) console.warn('[Image] fetch error', { url, error: String(err) });
    }
  };

  // Compute optimized sources for Supabase storage images (with caching)
  const { finalSrc, finalSrcSet, finalSizes } = useMemo(() => {
    const original = currentSrc;
    const result: { finalSrc?: string; finalSrcSet?: string; finalSizes?: string } = {
      finalSrc: original,
      finalSrcSet: srcSetProp as string | undefined,
      finalSizes: sizesProp as string | undefined,
    };

    // Auto-disable transforms for small images and logos
    const isSmallImage = (typeof explicitWidth === 'number' && typeof explicitHeight === 'number' && 
                        explicitWidth <= 64 && explicitHeight <= 64);
    
    // Also detect logo file patterns (avatars, logos, etc.)
    const isLogoFile = original && (
      original.includes('/logo.') ||
      original.includes('/avatar.') ||
      original.includes('/profile.') ||
      original.match(/\/(logo|avatar|profile|icon)\.(jpg|jpeg|png|webp|gif)$/i)
    );
    
    let effectiveOptimizationMode = optimizationMode;
    if ((isSmallImage || isLogoFile) && optimizationMode !== "none") {
      effectiveOptimizationMode = "none";
      if (isDebugEnabled()) {
        console.log('[Image] Auto-disabling transforms for small image/logo:', { 
          width: explicitWidth, 
          height: explicitHeight, 
          isSmallImage,
          isLogoFile,
          url: original 
        });
      }
    }

    // Only apply optimization when explicitly enabled AND not internally disabled AND transforms not globally disabled
    if (!optEnabled || disableTransforms || !original || !isSupabasePublicStorageUrl(original) || effectiveOptimizationMode === "none") {
      return result;
    }

    const renderParams: any = {};

    // Handle different optimization modes
    if (effectiveOptimizationMode === "format") {
      // Format-only optimization: WebP conversion + compression, no dimension changes
      renderParams.quality = 85;
      renderParams.format = "webp";
    } else if (effectiveOptimizationMode === "resize") {
      // Full optimization: dimensions + format + quality (current behavior)
      const targetWidth = typeof explicitWidth === 'number' && explicitWidth > 0 ? explicitWidth : 512;
      const targetHeight = typeof explicitHeight === 'number' && explicitHeight > 0 ? explicitHeight : undefined;

      renderParams.width = Math.min(320, Math.max(128, Math.round(targetWidth)));
      renderParams.quality = 85;

      // Add height and resize for better optimization
      if (targetHeight) {
        renderParams.height = Math.round(targetHeight);
      }
      if (resize) {
        renderParams.resize = resize;
      }
    }

    // Check cache first
    const cacheKey = getCacheKey(original, renderParams);
    const cached = urlCache.get(cacheKey);
    const now = Date.now();
    
    if (cached && (now - cached.timestamp) < CACHE_DURATION) {
      if (isDebugEnabled()) console.log('[Image] Using CACHED URLs:', { original, cached: cached.src });
      result.finalSrc = cached.src;
      result.finalSrcSet = cached.srcSet;
    } else {
      // Generate new URLs and cache them
      if (isDebugEnabled()) console.log('[Image] Generating NEW transform URLs:', { original, renderParams });
      result.finalSrc = getRenderUrlFromPublicUrl(original, renderParams);

      // Build responsive srcset only if base transform succeeded and in resize mode
      if (result.finalSrc !== original && !result.finalSrcSet && effectiveOptimizationMode === "resize") {
        const targetWidth = typeof explicitWidth === 'number' && explicitWidth > 0 ? explicitWidth : 512;
        const widths = Array.from(new Set([
          Math.round(targetWidth * 0.5),
          targetWidth,
          Math.round(targetWidth * 1.5),
          Math.min(1024, targetWidth * 2),
        ]))
          .filter((w): w is number => typeof w === 'number' && isFinite(w) && w >= 128)
          .sort((a, b) => a - b);
        
        result.finalSrcSet = buildSrcSetFromPublicUrl(original, widths, 85);
      }

      // Cache the results
      urlCache.set(cacheKey, {
        src: result.finalSrc || original,
        srcSet: result.finalSrcSet,
        timestamp: now
      });
    }

    // Default sizes
    if (!result.finalSizes) {
      result.finalSizes = typeof explicitWidth === 'number' && explicitWidth > 0
        ? `(max-width: ${explicitWidth}px) 100vw, ${explicitWidth}px`
        : '(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 300px';
    }

    return result;
  }, [currentSrc, objectFit, objectPosition, explicitWidth, explicitHeight, sizesProp, srcSetProp, optEnabled, disableTransforms, optimizationMode]);

  // Track what URL is actually being attempted for more accurate logging
  useEffect(() => {
    actualAttemptedUrl.current = finalSrc || currentSrc;
  }, [finalSrc, currentSrc]);

  useEffect(() => {
    // Reset states when src changes
    setHasError(false);
    setCurrentSrc(src);
    setOptEnabled(enableOptimization); // Reset internal optimization state
    setDisableTransforms(false); // Reset transform disable flag for new src
    loadStartTime.current = Date.now();
    attemptedFixRef.current = { triedRaw: false, triedRender: false, loggedFetch: false };

    if (instant) {
      // No spinner/fade for instant mode
      setIsLoading(false);
      setShowSpinner(false);
      if (spinnerTimeout.current) clearTimeout(spinnerTimeout.current);
      return;
    }

    setIsLoading(true);
    setShowSpinner(false);

    // Only show spinner after 100ms for fast-loading detection
    spinnerTimeout.current = setTimeout(() => {
      setShowSpinner(true);
    }, 100);

    return () => {
      if (spinnerTimeout.current) {
        clearTimeout(spinnerTimeout.current);
      }
    };
  }, [src, instant, enableOptimization]);

  const handleLoad = () => {
    const loadTime = Date.now() - loadStartTime.current;
    const attemptedUrl = actualAttemptedUrl.current || currentSrc;
    
    // Clear spinner timeout if image loads quickly
    if (spinnerTimeout.current) {
      clearTimeout(spinnerTimeout.current);
    }
    
    setIsLoading(false);
    setShowSpinner(false);
    
    // Enhanced logging to track optimization effectiveness
    const isOptimized = attemptedUrl?.includes('width=') || attemptedUrl?.includes('quality=');
    if (isDebugEnabled()) console.log(`[Image] Loaded ${isOptimized ? 'OPTIMIZED' : 'RAW'}: ${loadTime}ms - ${attemptedUrl}`);
    
    // Log slow loading images for debugging
    if (loadTime > 500 && isDebugEnabled()) {
      console.log(`[Image] SLOW LOAD: ${loadTime}ms for ${attemptedUrl || src}`);
    }
  };

  const handleError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    if (spinnerTimeout.current) {
      clearTimeout(spinnerTimeout.current);
    }

    const attemptedUrl = actualAttemptedUrl.current || currentSrc;
    
    // Enhanced debug log with actual attempted URL
    if (isDebugEnabled()) {
      console.warn('[Image] load error', { 
        attemptedUrl, 
        currentSrc, 
        finalSrc: finalSrc || currentSrc,
        optEnabled,
        disableTransforms,
        alt 
      });
    }

    // Run a diagnostic fetch to log HTTP status/headers and check for transform errors
    debugFetch(attemptedUrl).then(() => {
      // If this was a transform URL that failed, disable transforms globally to prevent retry loops
      if (attemptedUrl?.includes('width=') || attemptedUrl?.includes('quality=') || attemptedUrl?.includes('/render/image/')) {
        if (isDebugEnabled()) console.warn('[Image] Transform URL failed, disabling transforms for this session to prevent retry loops');
        setDisableTransforms(true);
      }
    });

    const url = currentSrc || '';

    // For Supabase images, try swapping between transform and raw object URLs
    if (url && isSupabasePublicStorageUrl(url)) {
      // If transform URL failed and we haven't tried raw yet, disable optimization and try raw
      if (attemptedUrl?.includes('width=') || attemptedUrl?.includes('quality=')) {
        if (!attemptedFixRef.current.triedRaw) {
          attemptedFixRef.current.triedRaw = true;
          if (isDebugEnabled()) console.log('[Image] Transform URL failed, trying raw URL');
          
          // Get the raw URL by removing transform params
          const rawUrl = url.split('?')[0];
          
          setOptEnabled(false); // Disable optimization for this attempt
          setHasError(false);
          setIsLoading(true);
          setShowSpinner(false);
          setCurrentSrc(rawUrl);
          loadStartTime.current = Date.now();
          return;
        }
      }
    }

    // Try fallback source once if provided
    if (fallbackSrc && currentSrc !== fallbackSrc) {
      setHasError(false);
      setIsLoading(true);
      setShowSpinner(false);
      setCurrentSrc(fallbackSrc);
      setOptEnabled(false); // Disable optimization for fallback
      loadStartTime.current = Date.now();
      return;
    }
    
    setIsLoading(false);
    setShowSpinner(false);
    setHasError(true);
    
    // Call any custom error handler
    if (onError) {
      onError(e);
    }
  };

  return (
    <div className={cn("relative overflow-hidden w-full h-full", className)}>
      {/* Show loading state only after delay and if still loading */}
      {!instant && showSpinner && isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-muted/50">
          {showSkeleton ? (
            <Skeleton className="w-full h-full" />
          ) : (
            <Spinner size="md" variant="muted" ariaLabel="Loading image" />
          )}
        </div>
      )}
      
      {hasError && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-muted/30">
          <ImageOff className="h-6 w-6 text-muted-foreground/60 mb-1" />
          <span className="text-xs text-muted-foreground/60">Image not available</span>
          {currentSrc && (
            <a
              href={currentSrc}
              target="_blank"
              rel="noreferrer"
              className="mt-1 text-[10px] underline text-muted-foreground/60"
              onClick={(e) => e.stopPropagation()}
            >
              Open image URL
            </a>
          )}
        </div>
      )}
      
      <img
        src={finalSrc || currentSrc}
        srcSet={finalSrcSet}
        sizes={finalSizes}
        alt={alt || ""}
        onLoad={handleLoad}
        onError={handleError}
        loading={(priority || instant) ? "eager" : loading}
        decoding="async"
        width={explicitWidth}
        height={explicitHeight}
        crossOrigin={optEnabled && isSupabasePublicStorageUrl((finalSrc || currentSrc) || '') ? "anonymous" : undefined}
        referrerPolicy={optEnabled && isSupabasePublicStorageUrl((finalSrc || currentSrc) || '') ? "no-referrer" : undefined}
        className={cn(
          "w-full h-full",
          !instant && "transition-opacity duration-150 ease-out",
          objectFit === "cover" ? "object-cover" : "object-contain",
          !instant && (isLoading || hasError ? "opacity-0" : "opacity-100"),
        )}
        style={{
          objectPosition: objectPosition,
          ...props.style
        }}
        {...props}
      />
    </div>
  );
};

export default Image;
